import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SEO from "../components/SEO";
import { useSSRData } from "../contexts/SSRDataContext";

interface ClassSummary {
  className: string;
  slug: string;
  drugCount: number;
  productCount: number;
  inStockCount: number;
  anyInStock: boolean;
}

interface DrugInClass {
  genericName: string;
  slug: string;
  productCount: number;
  anyInStock: boolean;
}

interface ClassDetail extends ClassSummary {
  drugs: DrugInClass[];
  manufacturers: string[];
  relatedClasses?: Array<{ className: string; slug: string; drugCount: number }>;
}

function TherapeuticClassIndexPage({ classes }: { classes: ClassSummary[] }) {
  const [search, setSearch] = useState("");
  const filtered = search.trim()
    ? classes.filter((c) => c.className.toLowerCase().includes(search.toLowerCase()))
    : classes;

  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const byLetter = new Map<string, ClassSummary[]>();
  for (const cls of filtered) {
    const letter = cls.className[0]?.toUpperCase() || "#";
    if (!byLetter.has(letter)) byLetter.set(letter, []);
    byLetter.get(letter)!.push(cls);
  }

  return (
    <>
      <SEO
        title="Therapeutic Drug Classes — Wholesale"
        description={`Browse ${classes.length} therapeutic drug classes available for wholesale from Premier Pharma. ACE inhibitors, beta blockers, statins, and more.`}
        path="/therapeutic-classes"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Therapeutic Classes", path: "/therapeutic-classes" },
        ]}
      />

      <section className="bg-gradient-to-br from-primary via-[#0a1e4a] to-[#0d2d6b] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-xs text-white/70 mb-3">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-white">Therapeutic Classes</span>
          </nav>
          <h1 className="text-3xl lg:text-4xl font-bold mb-3">Wholesale Drugs by Therapeutic Class</h1>
          <p className="text-white/80 text-sm sm:text-base mb-6">
            {classes.length.toLocaleString()} pharmacological classes · Licensed healthcare provider wholesale · DSCSA-compliant
          </p>
          <input
            type="search"
            aria-label="Search therapeutic classes"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search therapeutic classes…"
            className="w-full max-w-md bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-white/50 text-sm focus:outline-none focus:bg-white/20 focus:ring-2 focus:ring-white/60"
          />
        </div>
      </section>

      <section className="py-10 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {search.trim() ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filtered.map((cls) => (
                <ClassCard key={cls.slug} cls={cls} />
              ))}
            </div>
          ) : (
            <div className="space-y-8">
              {letters.map((letter) => {
                const group = byLetter.get(letter);
                if (!group || group.length === 0) return null;
                return (
                  <div key={letter}>
                    <h2 className="text-lg font-bold text-primary border-b border-gray-200 pb-1 mb-3">{letter}</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                      {group.map((cls) => <ClassCard key={cls.slug} cls={cls} />)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {filtered.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              <p className="text-lg font-medium mb-2">No classes found for "{search}"</p>
              <p className="text-sm">
                Try a broader search or{" "}
                <Link to="/drugs" className="text-accent hover:underline">browse the drug directory</Link>.
              </p>
            </div>
          )}

          <div className="mt-12 bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 text-center">
            <h2 className="text-xl font-bold text-primary mb-2">Ready to order at wholesale?</h2>
            <p className="text-gray-500 text-sm mb-5 max-w-2xl mx-auto">
              Create a free Premier Pharma account to unlock live wholesale pricing, place orders, and track shipments — approval typically takes minutes for licensed pharmacies, clinics, doctor's offices, and veterinary practices.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/register" className="bg-accent text-white px-5 py-2.5 rounded-lg font-medium hover:bg-accent/90">Create Account</Link>
              <Link to="/drugs" className="bg-white border border-gray-300 text-primary px-5 py-2.5 rounded-lg font-medium hover:bg-gray-50">Drug Directory</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function ClassCard({ cls }: { cls: ClassSummary }) {
  return (
    <Link
      to={`/therapeutic-classes/${cls.slug}`}
      className="bg-white border border-gray-200 rounded-lg p-3 hover:border-accent/40 hover:shadow-sm transition-all"
    >
      <div className="font-medium text-primary text-sm leading-tight">{cls.className}</div>
      <div className="text-xs text-gray-400 mt-1">{cls.drugCount} drugs · {cls.productCount} SKUs</div>
      {cls.anyInStock && (
        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-700 mt-1">
          <span className="w-1 h-1 rounded-full bg-green-500" />
          In Stock
        </span>
      )}
    </Link>
  );
}

function TherapeuticClassDetailPage({ classDetail }: { classDetail: ClassDetail }) {
  const { className, slug, drugCount, productCount, inStockCount, manufacturers, drugs, relatedClasses } = classDetail;

  const title = `${className} Wholesale Drugs — ${drugCount} Generics Available`;
  const description = `Buy wholesale ${className} drugs from Premier Pharma. ${drugCount} generic drugs, ${productCount} SKUs${inStockCount > 0 ? `, ${inStockCount} in stock` : ""}. DSCSA-compliant, same-day shipping for licensed pharmacies, clinics, and veterinary practices.`;

  // ── CollectionPage structured data (schema.org/CollectionPage) ──────────
  // Mirrors the server-side JSON-LD built in server/therapeuticClassPages.ts
  // so the markup is also present after React hydration and SPA navigation.
  const classPageJsonLd: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${className} Wholesale Drugs`,
    description,
    url: `https://premierpharma.com/therapeutic-classes/${slug}`,
    numberOfItems: productCount,
    isPartOf: { "@id": "https://premierpharma.com/#website" },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: drugs.length,
      itemListElement: drugs.slice(0, 20).map((d, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: d.genericName,
        url: `https://premierpharma.com/drugs/${d.slug}`,
      })),
    },
  };

  // ── FAQPage structured data (schema.org/FAQPage) ─────────────────────────
  // Mirrors the server-side FAQ JSON-LD in server/therapeuticClassPages.ts.
  const nameLC = className.toLowerCase();
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `Where can I buy wholesale ${nameLC} drugs for my pharmacy?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `Premier Pharma stocks ${productCount.toLocaleString()}+ ${nameLC} SKUs across ${drugCount} generic drugs at competitive wholesale pricing for licensed pharmacies, clinics, and healthcare facilities across the United States. Browse the catalog above or create a free account to see live pricing.`,
        },
      },
      {
        "@type": "Question",
        name: `Is Premier Pharma a licensed ${nameLC} distributor?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `Yes. Premier Pharma is an NABP-accredited pharmaceutical distributor and operates in full compliance with the Drug Supply Chain Security Act (DSCSA). Every shipment includes the required transaction information, history, and statement.`,
        },
      },
      {
        "@type": "Question",
        name: `How quickly do ${nameLC} orders ship?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `In-stock items ship the same business day when ordered before our cutoff. Items not currently in stock can usually be sourced and delivered within two business days through our partner network — just request 2-day shipping from the product page.`,
        },
      },
      {
        "@type": "Question",
        name: `Do I need an account to see ${nameLC} pricing?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `Yes. To comply with state and federal regulations, wholesale pricing is only visible to verified licensed buyers. Account approval typically takes minutes once your DEA, state license, and NPI are submitted.`,
        },
      },
    ],
  };

  return (
    <>
      <SEO
        title={title}
        description={description}
        path={`/therapeutic-classes/${slug}`}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Therapeutic Classes", path: "/therapeutic-classes" },
          { name: className, path: `/therapeutic-classes/${slug}` },
        ]}
        jsonLd={[classPageJsonLd, faqJsonLd]}
      />

      <section className="bg-gradient-to-br from-primary via-[#0a1e4a] to-[#0d2d6b] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-xs text-white/70 mb-3">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/therapeutic-classes" className="hover:text-white">Therapeutic Classes</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{className}</span>
          </nav>
          <h1 className="text-3xl lg:text-4xl font-bold leading-tight mb-2">
            {className} Wholesale
          </h1>
          <p className="text-white/80 text-sm sm:text-base">
            {drugCount} generic drugs · {productCount} SKUs available
            {inStockCount > 0 && ` · ${inStockCount} in stock`}
            {" · "}NABP accredited · DSCSA compliant
          </p>
        </div>
      </section>

      <section className="py-10 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label="Generic Drugs" value={String(drugCount)} />
            <StatCard label="Total SKUs" value={String(productCount)} />
            <StatCard label="In Stock" value={String(inStockCount)} highlight={inStockCount > 0} />
            <StatCard label="Manufacturers" value={String(manufacturers.length)} />
          </div>

          {/* Manufacturers */}
          {manufacturers.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Manufacturers</h2>
              <div className="flex flex-wrap gap-2">
                {manufacturers.map((m) => (
                  <span key={m} className="bg-gray-100 text-gray-700 text-sm px-2.5 py-1 rounded-full">{m}</span>
                ))}
              </div>
            </div>
          )}

          {/* Data-driven intro */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-5">
            <p className="text-gray-700 text-sm leading-relaxed">
              Premier Pharma stocks <strong>{drugCount} generic {className.toLowerCase()} drugs</strong> across{" "}
              <strong>{productCount} SKUs</strong>
              {inStockCount > 0 && (
                <>, with <strong>{inStockCount} drug{inStockCount !== 1 ? "s" : ""} currently in stock</ strong> and available for same-day shipment</>
              )}.
              {manufacturers.length > 0 && (
                <> Available from manufacturers including {manufacturers.slice(0, 3).join(", ")}{manufacturers.length > 3 ? ` and ${manufacturers.length - 3} more` : ""}.</>
              )}{" "}
              Licensed pharmacies, clinics, doctor's offices, and veterinary practices can place orders with DSCSA-compliant chain-of-custody documentation.
            </p>
          </div>

          {/* Drug list */}
          <div>
            <h2 className="text-xl font-bold text-primary mb-4">
              Generic {className} Drugs
            </h2>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Generic Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">SKUs</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {drugs.map((drug) => (
                    <tr key={drug.slug} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <Link
                          to={`/drugs/${drug.slug}`}
                          className="text-primary hover:text-accent font-medium text-sm"
                        >
                          {drug.genericName}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs hidden sm:table-cell">
                        {drug.productCount} SKU{drug.productCount !== 1 ? "s" : ""}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${drug.anyInStock ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-600"}`}>
                          <span className={`w-1 h-1 rounded-full ${drug.anyInStock ? "bg-green-500" : "bg-blue-400"}`} />
                          {drug.anyInStock ? "In Stock" : "2-Day"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Related Therapeutic Areas */}
          {relatedClasses && relatedClasses.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Related Therapeutic Areas</h2>
              <div className="flex flex-wrap gap-2">
                {relatedClasses.map((cls) => (
                  <Link
                    key={cls.slug}
                    to={`/therapeutic-classes/${cls.slug}`}
                    className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 hover:border-accent/40 hover:bg-blue-50 rounded-full px-3 py-1 text-sm text-primary hover:text-accent transition-colors"
                  >
                    {cls.className}
                    <span className="text-xs text-gray-400">({cls.drugCount})</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* FAQ */}
          <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
            <h2 className="text-lg font-bold text-primary px-5 py-4">Frequently Asked Questions</h2>
            {[
              {
                q: `Where can I buy wholesale ${nameLC} drugs for my pharmacy?`,
                a: `Premier Pharma stocks ${productCount.toLocaleString()}+ ${nameLC} SKUs across ${drugCount} generic drugs at competitive wholesale pricing for licensed pharmacies, clinics, and healthcare facilities across the United States. Browse the catalog above or create a free account to see live pricing.`,
              },
              {
                q: `Is Premier Pharma a licensed ${nameLC} distributor?`,
                a: `Yes. Premier Pharma is an NABP-accredited pharmaceutical distributor and operates in full compliance with the Drug Supply Chain Security Act (DSCSA). Every shipment includes the required transaction information, history, and statement.`,
              },
              {
                q: `How quickly do ${nameLC} orders ship?`,
                a: `In-stock items ship the same business day when ordered before our cutoff. Items not currently in stock can usually be sourced and delivered within two business days through our partner network — just request 2-day shipping from the product page.`,
              },
              {
                q: `Do I need an account to see ${nameLC} pricing?`,
                a: `Yes. To comply with state and federal regulations, wholesale pricing is only visible to verified licensed buyers. Account approval typically takes minutes once your DEA, state license, and NPI are submitted.`,
              },
            ].map(({ q, a }, i) => (
              <details key={i} className="group px-5 py-4">
                <summary className="cursor-pointer font-semibold text-sm text-primary list-none flex items-center justify-between gap-3">
                  {q}
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0 transition-transform group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </summary>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>

          {/* CTA */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 text-center">
            <h2 className="text-xl font-bold text-primary mb-2">
              Ready to order {className.toLowerCase()} drugs at wholesale?
            </h2>
            <p className="text-gray-500 text-sm mb-5 max-w-2xl mx-auto">
              Create a free Premier Pharma account to unlock live wholesale pricing, place orders, and track shipments — approval typically takes minutes for licensed pharmacies, clinics, doctor's offices, and veterinary practices.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/register" className="bg-accent text-white px-5 py-2.5 rounded-lg font-medium hover:bg-accent/90">Create Account</Link>
              <Link to="/therapeutic-classes" className="bg-white border border-gray-300 text-primary px-5 py-2.5 rounded-lg font-medium hover:bg-gray-50">All Therapeutic Classes</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function StatCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
      <div className={`text-2xl font-bold mb-1 ${highlight ? "text-green-600" : "text-primary"}`}>{value}</div>
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}

export default function TherapeuticClassLanding() {
  const params = useParams<{ slug?: string }>();
  const slug = params.slug;
  const { seoData } = useSSRData();

  const [data, setData] = useState<
    | { type: "therapeutic-class-index"; classes: ClassSummary[] }
    | { type: "therapeutic-class-detail"; classDetail: ClassDetail }
    | null
  >(
    seoData?.type === "therapeutic-class-index" || seoData?.type === "therapeutic-class-detail"
      ? (seoData as any)
      : null,
  );
  const [loading, setLoading] = useState(!data);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const alreadyLoaded =
      (data?.type === "therapeutic-class-index" && !slug) ||
      (data?.type === "therapeutic-class-detail" && slug &&
        (data as any).classDetail?.slug === slug);

    if (alreadyLoaded) return;

    setLoading(true);
    setNotFound(false);

    const url = slug
      ? `/api/seo/therapeutic-classes/${encodeURIComponent(slug)}`
      : "/api/seo/therapeutic-classes";

    fetch(url)
      .then((r) => {
        if (r.status === 404) { setNotFound(true); return null; }
        if (!r.ok) throw new Error("Failed");
        return r.json();
      })
      .then((d) => { if (d) setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  }

  if (notFound || !data) {
    return (
      <>
        <SEO title="Therapeutic Class Not Found" noindex />
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-2xl font-bold text-primary mb-2">Class not found</h1>
          <p className="text-gray-500 mb-4">Browse all therapeutic classes or search the drug directory.</p>
          <div className="flex gap-3">
            <Link to="/therapeutic-classes" className="bg-accent text-white px-5 py-2 rounded-lg font-medium hover:bg-accent/90">
              Therapeutic Classes
            </Link>
            <Link to="/drugs" className="bg-white border border-gray-300 text-primary px-5 py-2 rounded-lg font-medium hover:bg-gray-50">
              Drug Directory
            </Link>
          </div>
        </div>
      </>
    );
  }

  if (data.type === "therapeutic-class-index") {
    return <TherapeuticClassIndexPage classes={data.classes} />;
  }

  if (data.type === "therapeutic-class-detail") {
    return <TherapeuticClassDetailPage classDetail={(data as any).classDetail} />;
  }

  return null;
}
